import React, { useRef, useCallback, useState } from 'react'
import { Button, Space, Typography, Modal, Select, notification, Spin } from 'antd'
import { UploadOutlined, InfoCircleOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { UploadButtonProps } from '../types'
import { useFileUpload } from '../hooks/useFileUpload'
import { MultipleUploadProgress } from './UploadProgress'
import { formatFileSize, sanitizeFilename } from '../utils/file-upload-api'

const { Text } = Typography
const { Option } = Select

const UploadContainer = styled.div`
  .upload-button {
    position: relative;
    overflow: hidden;
  }

  .upload-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
`

const UsageTypeContainer = styled.div`
  margin-top: 16px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
`

const UsageTypeInfo = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #e6f7ff;
  border-radius: 4px;
  border-left: 3px solid #1890ff;
`

interface UploadButtonComponentProps extends UploadButtonProps {
  showUsageSelector?: boolean
  showProgressModal?: boolean
  autoSelectUsage?: string
  // When true, loading/error states render as a small disabled button
  // instead of a full-width spinner or Alert block. Use in compact
  // headers/rows where an inline error banner would break the layout.
  compact?: boolean
}

/**
 * Button component for file uploads with backend-driven configuration
 */
export const UploadButton: React.FC<UploadButtonComponentProps> = ({
  projectId,
  urbanAnalysisId,
  usage,
  allowedUsageTypes,
  onUploadStart,
  onUploadProgress,
  onUploadComplete,
  onUploadError,
  disabled = false,
  maxFileSize,
  acceptedMimeTypes,
  multiple = false,
  children,
  buttonProps = {},
  showUsageSelector = false,
  showProgressModal = true,
  autoSelectUsage,
  compact = false,
}) => {
  // State management
  const [selectedUsage, setSelectedUsage] = useState<string>(
    autoSelectUsage || usage || ''
  )
  const [showProgress, setShowProgress] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // File upload hook with backend configuration
  const {
    uploadFile,
    uploadFiles,
    validateFile,
    isUploading,
    uploadProgress,
    errors,
    clearErrors,
    configuration,
    isLoadingConfig,
  } = useFileUpload({
    projectId,
    urbanAnalysisId,
    onUploadStart: (file) => {
      if (showProgressModal) {
        setShowProgress(true)
      }
      onUploadStart?.(file)
    },
    onUploadProgress,
    onUploadComplete: (result) => {
      onUploadComplete?.(result)
      if (result.success) {
        notification.success({
          message: 'Téléchargement réussi',
          description: `Le fichier "${result.filename}" a été téléchargé avec succès.`,
        })
      }
    },
    onUploadError: (error, file) => {
      onUploadError?.(error, file)
      notification.error({
        message: 'Erreur de téléchargement',
        description: error,
      })
    },
  })

  // Get available usage types from configuration
  const availableUsageTypes = React.useMemo(() => {
    if (!configuration) return []

    const allUsageTypes = Object.values(configuration.usageTypes)

    if (allowedUsageTypes) {
      return allUsageTypes.filter(ut => allowedUsageTypes.includes(ut.usage))
    }

    return allUsageTypes
  }, [configuration, allowedUsageTypes])

  // Get current usage metadata
  const currentUsageMetadata = React.useMemo(() => {
    if (!configuration || !selectedUsage) return null
    return configuration.usageTypes[selectedUsage] || null
  }, [configuration, selectedUsage])

  /**
   * Handles file selection from input
   */
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return

    // A usage type is mandatory only when a selector is shown for the user to
    // pick one. Without a selector (e.g. the generic "Uploader" button), an
    // empty usage is an intentional *unassigned* upload — the file lands in the
    // to-sort inbox (use = NULL) for the architect to triage later.
    if (showUsageSelector && !selectedUsage) {
      notification.error({
        message: 'Type de document requis',
        description: 'Veuillez sélectionner un type de document avant de télécharger un fichier.',
      })
      return
    }

    // Clear previous errors
    clearErrors()

    // Validate files before uploading. Filenames are transparently sanitized
    // to match the backend's allowed character set so commas, accents, etc.
    // don't block the user from uploading.
    const validFiles: File[] = []
    const invalidFiles: { file: File; errors: string[] }[] = []

    for (const rawFile of files) {
      const safeName = sanitizeFilename(rawFile.name, configuration ?? undefined)
      const file =
        safeName === rawFile.name
          ? rawFile
          : new File([rawFile], safeName, { type: rawFile.type, lastModified: rawFile.lastModified })
      const validation = validateFile(file, selectedUsage)
      if (validation.isValid) {
        validFiles.push(file)
      } else {
        invalidFiles.push({ file, errors: validation.errors })
      }
    }

    // Show errors for invalid files
    if (invalidFiles.length > 0) {
      invalidFiles.forEach(({ file, errors }) => {
        notification.error({
          message: `Fichier invalide: ${file.name}`,
          description: errors.join(', '),
        })
      })
    }

    // Upload valid files
    if (validFiles.length > 0) {
      try {
        if (multiple) {
          await uploadFiles(validFiles, selectedUsage)
        } else {
          await uploadFile(validFiles[0], selectedUsage)
        }
      } catch (error) {
        console.error('Upload error:', error)
      }
    }

    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [selectedUsage, showUsageSelector, configuration, validateFile, uploadFile, uploadFiles, multiple, clearErrors])

  /**
   * Triggers file input click
   */
  const handleButtonClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [])

  /**
   * Gets accepted file types for input
   */
  const getAcceptedTypes = useCallback(() => {
    if (acceptedMimeTypes) {
      return acceptedMimeTypes.join(',')
    }
    if (currentUsageMetadata) {
      return currentUsageMetadata.allowedTypes.join(',')
    }
    return '*/*'
  }, [acceptedMimeTypes, currentUsageMetadata])

  /**
   * Gets max file size
   */
  const getMaxFileSize = useCallback(() => {
    if (maxFileSize) return maxFileSize
    if (currentUsageMetadata) return currentUsageMetadata.maxSize
    return 50 * 1024 * 1024 // Default 50MB
  }, [maxFileSize, currentUsageMetadata])

  /**
   * Handles usage type change
   */
  const handleUsageChange = useCallback((value: string) => {
    setSelectedUsage(value)
  }, [])

  /**
   * Closes progress modal
   */
  const handleCloseProgress = useCallback(() => {
    setShowProgress(false)
  }, [])

  // Loading state
  if (isLoadingConfig) {
    if (compact) return null
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '8px' }}>
          <Text type="secondary">Chargement de la configuration...</Text>
        </div>
      </div>
    )
  }

  // Error state — render a disabled button instead of an intrusive Alert
  // so the page layout is not disrupted while the config is unavailable.
  if (!configuration) {
    if (compact) return null
    return (
      <Button icon={<UploadOutlined />} disabled {...buttonProps}>
        {children ?? (multiple ? 'Sélectionner des fichiers' : 'Sélectionner un fichier')}
      </Button>
    )
  }

  // In compact mode, when the current usage is not in the backend's allowed set
  // we still render the button — the upload flow and validation remain functional
  // via the default constraints. Hiding it would block uploads for valid usages
  // (CERFA, PROJECT_SHEET, etc.) that are accepted by the backend but not
  // explicitly listed in the config's usageTypes subset.

  return (
    <UploadContainer>
      {showUsageSelector && (
        <UsageTypeContainer>
          <Text strong>Type de document :</Text>
          <Select
            value={selectedUsage}
            onChange={handleUsageChange}
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Sélectionnez le type de document"
          >
            {availableUsageTypes.map((metadata) => (
              <Option key={metadata.usage} value={metadata.usage}>
                {metadata.label}
              </Option>
            ))}
          </Select>

          {selectedUsage && currentUsageMetadata && (
            <UsageTypeInfo>
              <Space direction="vertical" size="small">
                <Text>
                  <InfoCircleOutlined style={{ marginRight: 4 }} />
                  {currentUsageMetadata.description}
                </Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Types acceptés: {currentUsageMetadata.allowedTypes.join(', ')}
                </Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Taille maximum: {formatFileSize(getMaxFileSize())}
                </Text>
              </Space>
            </UsageTypeInfo>
          )}
        </UsageTypeContainer>
      )}

      <div className="upload-button">
        <Button
          icon={<UploadOutlined />}
          onClick={handleButtonClick}
          disabled={disabled || isUploading || (showUsageSelector && !selectedUsage)}
          loading={isUploading}
          {...buttonProps}
        >
          {children ?? (compact ? null : multiple ? 'Sélectionner des fichiers' : 'Sélectionner un fichier')}
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          className="upload-input"
          accept={getAcceptedTypes()}
          multiple={multiple}
          onChange={handleFileSelect}
          disabled={disabled || isUploading}
        />
      </div>

      {/* Progress Modal */}
      {showProgressModal && (
        <Modal
          title="Téléchargement en cours"
          open={showProgress}
          onCancel={handleCloseProgress}
          footer={null}
          width={600}
          destroyOnClose
        >
          <MultipleUploadProgress
            progressItems={uploadProgress}
            showDetails={true}
            compact={false}
          />

          {errors.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text type="danger">Erreurs :</Text>
              <ul>
                {errors.map((error, index) => (
                  <li key={index}>
                    <Text type="danger">{error}</Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Modal>
      )}
    </UploadContainer>
  )
}

/**
 * Simple upload button without usage selector
 */
interface SimpleUploadButtonProps extends Omit<UploadButtonProps, 'usage'> {
  usage: string
}

export const SimpleUploadButton: React.FC<SimpleUploadButtonProps> = (props) => {
  return (
    <UploadButton
      {...props}
      showUsageSelector={false}
      showProgressModal={false}
    />
  )
}

/**
 * Upload button with usage selector
 */
export const UploadButtonWithSelector: React.FC<Omit<UploadButtonComponentProps, 'usage'>> = (props) => {
  return (
    <UploadButton
      {...props}
      showUsageSelector={true}
      showProgressModal={true}
    />
  )
}
